// Kleine Fundstücke in der Landschaft. Ein Klick darauf zeigt einen Spruch.
import * as THREE from "three"
import { COLORS } from "./palette.js"
import { at, group, box, cylinder, house, prism, cow, person, mat } from "./builders.js"
import { heightAt, LAKE, PEAK } from "./terrain.js"

const WOOD = 0x8a5a36
const HIT_MATERIAL = new THREE.MeshBasicMaterial({ visible: false })

// Unsichtbare Klickzone, damit auch kleine Fundstücke leicht zu treffen sind.
function hitZone(radius, height, y = 0) {
  const zone = new THREE.Mesh(new THREE.CylinderGeometry(radius, radius, height, 10), HIT_MATERIAL)
  zone.position.y = y + height / 2
  return zone
}
const STONE = 0xb7b2a6

export const EGG_TEXTS = {
  cabin: {
    title: "Seehütte mit Sauna",
    text: "Geheizt wird mit Holz. Biogenes CO₂ weist das GHG Protocol übrigens getrennt von Scope 1–3 aus."
  },
  campfire: {
    title: "Lagerfeuer",
    text: "Das Feuer ist Scope 1. Die Marshmallows sind Scope 3, Kategorie 1: eingekaufte Waren."
  },
  castle: {
    title: "Burgruine",
    text: "Seit Jahrhunderten ohne Heizung. Sehr sparsam – aber nicht sehr gemütlich."
  },
  ufo: {
    title: "Unbekanntes Flugobjekt",
    text: "Selbst Außerirdische fragen inzwischen nach Ihren Scope-3-Daten. Die Kuh gehört zu Kategorie 1."
  },
  deer: {
    title: "Hirsche",
    text: "Diese Belegschaft pendelt emissionsfrei. Bei Ihrer Belegschaft ist das Kategorie 7: Pendeln."
  },
  summit: {
    title: "Gipfelkreuz",
    text: "Gipfel erreicht! Von hier oben sieht man die ganze Wertschöpfungskette."
  },
  balloon: {
    title: "be2morrow-Ballon",
    text: "Heiße Luft gibt’s hier nur im Ballon – Ihre Bilanz bauen wir aus echten Daten."
  },
  sheep: {
    title: "Schafherde",
    text: "Mäht die Wiese ganz ohne Diesel-Rasenmäher. Nur die Methanbilanz ist noch ausbaufähig."
  },
  windmill: {
    title: "Alte Windmühle",
    text: "Erneuerbare Energie mit Tradition. Heute ist Wind die wichtigste Stromquelle in Deutschland."
  },
  bees: {
    title: "Bienenstöcke",
    text: "Die fleißigste Belegschaft im Tal – und keine einzige Dienstreise mit dem Flugzeug (Kategorie 6)."
  },
  snowman: {
    title: "Schneemann",
    text: "Klimaneutral, aber nicht klimaresilient. Auch Klimarisiken für Ihr Unternehmen gehören in den Bericht."
  }
}

function onGround(object, x, z, offset = 0) {
  object.position.set(x, heightAt(x, z) + offset, z)
  return object
}

function tent(color) {
  const t = prism(2.2, 1.6, 2.4, color)
  t.rotation.y = Math.PI / 2
  return t
}

function deer({ grazing = false, antlers = false } = {}) {
  const brown = 0x9b6b43
  const g = group(at(box(1.2, 0.5, 0.45, brown), 0, 0.75, 0))
  for (const [x, z] of [[-0.45, 0.15], [0.45, 0.15], [-0.45, -0.15], [0.45, -0.15]]) g.add(at(box(0.1, 0.75, 0.1, 0x6e4a2e), x, 0, z))
  const neck = at(box(0.2, 0.6, 0.2, brown), 0.55, 1.05, 0)
  neck.rotation.z = grazing ? -2.2 : -0.4
  g.add(neck)
  const head = at(box(0.42, 0.24, 0.24, brown), grazing ? 0.95 : 0.78, grazing ? 0.45 : 1.45, 0)
  g.add(head)
  if (antlers) {
    for (const z of [-0.12, 0.12]) {
      const antler = at(box(0.05, 0.45, 0.05, 0xd9c7a3), 0.75, 1.62, z)
      antler.rotation.x = z * 3
      g.add(antler)
    }
  }
  return g
}

function sheep() {
  const wool = new THREE.Mesh(new THREE.IcosahedronGeometry(0.5, 0), mat(0xf6f4ee))
  wool.scale.set(1.3, 0.9, 0.9)
  wool.position.y = 0.75
  wool.castShadow = true
  const g = group(wool, at(box(0.3, 0.3, 0.28, COLORS.navy), 0.7, 0.75, 0))
  for (const [x, z] of [[-0.3, 0.18], [0.3, 0.18], [-0.3, -0.18], [0.3, -0.18]]) g.add(at(box(0.09, 0.45, 0.09, COLORS.navy), x, 0, z))
  return g
}

export function buildEasterEggs() {
  const root = new THREE.Group()
  const eggs = []
  const updaters = []
  const keepOut = []
  const register = (id, object, x, z, radius = 9, hit = { r: 5, h: 5, y: 0 }) => {
    object.userData.egg = { id, ...EGG_TEXTS[id] }
    object.add(hitZone(hit.r, hit.h, hit.y))
    root.add(object)
    eggs.push(object)
    keepOut.push({ x, z, r: radius })
    return object
  }

  // Seehütte mit Steg und Ruderboot
  {
    const x = LAKE.x - 4
    const z = LAKE.z - LAKE.r - 5
    const cabin = group(house(0xa8784f, 0x5b3a24))
    cabin.add(at(cylinder(0.2, 0.2, 1.2, COLORS.steelDark, 6), 0.5, 2, -0.4))
    const jetty = at(box(1.2, 0.15, 6, WOOD), 0.8, LAKE.level - heightAt(x, z) + 0.35, 4.5)
    cabin.add(jetty)
    const boat = group(at(box(1.8, 0.4, 0.8, 0xc0703f), 0, 0, 0), at(box(0.4, 0.1, 0.7, WOOD), 0, 0.4, 0))
    boat.position.set(3, LAKE.level - heightAt(x, z) + 0.05, 6.5)
    cabin.add(boat)
    updaters.push((dt, time) => {
      boat.position.y = LAKE.level - heightAt(x, z) + 0.05 + Math.sin(time * 1.6) * 0.08
      boat.rotation.z = Math.sin(time * 1.3) * 0.05
    })
    register("cabin", onGround(cabin, x, z), x, z, 8, { r: 5, h: 5, y: 0 })
    cabin.userData.smokeAt = new THREE.Vector3(x + 0.5, cabin.position.y + 3.3, z - 0.4)
  }

  // Lagerfeuer mit Zelten
  {
    const x = -92
    const z = 46
    const camp = group()
    for (let i = 0; i < 4; i++) {
      const log = at(box(1.2, 0.2, 0.2, WOOD), 0, 0.1, 0)
      log.rotation.y = (i * Math.PI) / 4
      camp.add(log)
    }
    const flame = new THREE.Mesh(new THREE.ConeGeometry(0.45, 1.2, 6), mat(COLORS.orange, { emissive: COLORS.orange, emissiveIntensity: 1.2 }))
    flame.position.y = 0.75
    camp.add(flame)
    const inner = new THREE.Mesh(new THREE.ConeGeometry(0.25, 0.8, 5), mat(COLORS.lime, { emissive: COLORS.lime, emissiveIntensity: 1 }))
    inner.position.y = 0.55
    camp.add(inner)
    camp.add(at(tent(COLORS.green), -3, 0, -2))
    camp.add(at(tent(COLORS.orange), 2.8, 0, -2.6))
    camp.add(at(person({ shirt: COLORS.red }), -1.4, 0, 1.2))
    camp.add(at(person({ shirt: COLORS.lime }), 1.5, 0, 1))
    updaters.push((dt, time) => {
      const flicker = 1 + Math.sin(time * 17) * 0.08 + Math.sin(time * 23) * 0.06
      flame.scale.set(flicker, 1 + Math.sin(time * 11) * 0.15, flicker)
    })
    register("campfire", onGround(camp, x, z), x, z, 7, { r: 5, h: 3.5, y: 0 })
    camp.userData.smokeAt = new THREE.Vector3(x, camp.position.y + 1.4, z)
  }

  // Burgruine auf einem Hügel
  {
    const x = -104
    const z = -64
    const ruin = group()
    const tower = cylinder(2, 2.3, 8, STONE, 8)
    ruin.add(tower)
    for (let i = 0; i < 8; i += 2) {
      const a = (i / 8) * Math.PI * 2
      ruin.add(at(box(0.7, 0.8, 0.7, STONE), Math.cos(a) * 1.8, 8, Math.sin(a) * 1.8))
    }
    for (const [wx, wz, w, d, h] of [[4, 0, 6, 0.8, 3.2], [7, 3, 0.8, 6, 2.2], [1.5, 5, 4, 0.8, 1.4]]) ruin.add(at(box(w, h, d, STONE), wx, 0, wz))
    for (const [bx, bz] of [[5, 5], [3.5, 6.5], [8, -1.5]]) ruin.add(at(box(0.8, 0.6, 0.8, 0xa29c8f), bx, 0, bz))
    ruin.add(at(box(0.05, 1.6, 0.05, COLORS.white), 0, 8.8, 0))
    ruin.add(at(box(0.8, 0.5, 0.04, COLORS.green), 0.4, 9.8, 0))
    register("castle", onGround(ruin, x, z), x, z, 11, { r: 6, h: 10, y: 0 })
  }

  // Ufo, das eine Kuh hochbeamt
  {
    const x = -58
    const z = -116
    const ground = heightAt(x, z)
    const ufo = new THREE.Group()
    const saucer = group(cylinder(3.4, 2.2, 0.6, COLORS.steel, 16))
    const dome = new THREE.Mesh(new THREE.SphereGeometry(1.4, 12, 6, 0, Math.PI * 2, 0, Math.PI / 2), mat(COLORS.glass, { metalness: 0.3, roughness: 0.2 }))
    dome.position.y = 0.6
    saucer.add(dome)
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2
      saucer.add(at(box(0.3, 0.2, 0.3, COLORS.lime, { emissive: COLORS.lime, emissiveIntensity: 1 }), Math.cos(a) * 2.9, 0.15, Math.sin(a) * 2.9))
    }
    saucer.position.y = ground + 14
    const beamHeight = 14
    const beam = new THREE.Mesh(
      new THREE.CylinderGeometry(1.2, 2.8, beamHeight, 16, 1, true),
      new THREE.MeshBasicMaterial({ color: COLORS.lime, transparent: true, opacity: 0.22, side: THREE.DoubleSide, depthWrite: false })
    )
    beam.position.y = ground + beamHeight / 2
    const victim = cow()
    victim.scale.setScalar(1.4)
    ufo.add(saucer, beam, victim)
    ufo.position.set(x, 0, z)
    updaters.push((dt, time) => {
      saucer.rotation.y += dt * 1.2
      saucer.position.y = ground + 14 + Math.sin(time * 1.4) * 0.4
      const lift = (Math.sin(time * 0.6) + 1) / 2
      victim.position.y = ground + 0.2 + lift * 8
      victim.rotation.y += dt * 0.8
      victim.rotation.z = Math.sin(time * 2) * 0.3
      beam.material.opacity = 0.16 + Math.sin(time * 5) * 0.05
    })
    register("ufo", ufo, x, z, 8, { r: 4, h: 17, y: ground })
  }

  // Hirsche auf einer Lichtung
  {
    const x = 74
    const z = -98
    const herd = group()
    for (const [dx, dz, r, options] of [[0, 0, 0.4, { antlers: true }], [2.6, 1.8, 2.2, { grazing: true }], [-2.2, 2.4, 1.2, { grazing: true }]]) {
      const d = deer(options)
      d.position.set(dx, heightAt(x + dx, z + dz) - heightAt(x, z), dz)
      d.rotation.y = r
      d.scale.setScalar(1.4)
      herd.add(d)
    }
    register("deer", onGround(herd, x, z), x, z, 8, { r: 5, h: 4, y: 0 })
  }

  // Gipfelkreuz auf dem höchsten Berg
  {
    let best = { x: PEAK.x, z: PEAK.z, h: -Infinity }
    for (let dx = -20; dx <= 20; dx += 2) {
      for (let dz = -20; dz <= 20; dz += 2) {
        const h = heightAt(PEAK.x + dx, PEAK.z + dz)
        if (h > best.h) best = { x: PEAK.x + dx, z: PEAK.z + dz, h }
      }
    }
    const cross = group(box(0.35, 4.5, 0.35, WOOD), at(box(2.2, 0.3, 0.3, WOOD), 0, 3.2, 0))
    cross.add(at(box(1.1, 0.7, 0.05, COLORS.green), 0.75, 3.8, 0.2))
    cross.scale.setScalar(1.6)
    register("summit", onGround(cross, best.x, best.z, -0.2), best.x, best.z, 6, { r: 2.5, h: 6, y: 0 })
  }

  // Schafherde mit Schäferin
  {
    const x = -34
    const z = 92
    const flock = group()
    const animals = []
    for (let i = 0; i < 7; i++) {
      const s = sheep()
      const dx = Math.cos(i * 2.4) * (2 + (i % 3) * 1.5)
      const dz = Math.sin(i * 2.4) * (2 + (i % 3) * 1.2)
      s.position.set(dx, heightAt(x + dx, z + dz) - heightAt(x, z), dz)
      s.rotation.y = i * 1.3
      s.scale.setScalar(1.3)
      s.userData.base = s.position.clone()
      animals.push(s)
      flock.add(s)
    }
    const shepherd = person({ shirt: COLORS.green, helmet: WOOD })
    shepherd.scale.setScalar(1.4)
    shepherd.position.set(-4.5, heightAt(x - 4.5, z) - heightAt(x, z), 1)
    flock.add(shepherd)
    updaters.push((dt, time) => {
      animals.forEach((s, i) => {
        s.position.y = s.userData.base.y + Math.max(0, Math.sin(time * 3 + i * 1.7)) * 0.12
        s.rotation.y += Math.sin(time * 0.4 + i) * 0.003
      })
    })
    register("sheep", onGround(flock, x, z), x, z, 9, { r: 7, h: 3, y: 0 })
  }

  // Alte Windmühle mit drehenden Flügeln
  {
    const x = 42
    const z = 98
    const mill = group(cylinder(1.4, 2.1, 6.5, COLORS.wallWarm, 8))
    const cap = new THREE.Mesh(new THREE.ConeGeometry(1.9, 2.2, 8), mat(0x5b3a24))
    cap.position.y = 7.6
    cap.castShadow = true
    mill.add(cap)
    mill.add(at(box(0.8, 1.2, 0.1, 0x5b3a24), 0, 0, 2.02))
    const sails = new THREE.Group()
    sails.position.set(0, 6.9, 1.9)
    for (let i = 0; i < 4; i++) {
      const arm = new THREE.Group()
      arm.rotation.z = (i * Math.PI) / 2
      arm.add(at(box(0.18, 5.2, 0.12, WOOD), 0, 0.2, 0))
      const sail = at(box(1.3, 3.8, 0.06, COLORS.white), 0.75, 1.3, 0.05)
      arm.add(sail)
      sails.add(arm)
    }
    sails.add(new THREE.Mesh(new THREE.SphereGeometry(0.3, 6, 5), mat(WOOD)))
    mill.add(sails)
    mill.rotation.y = -0.5
    updaters.push((dt) => (sails.rotation.z -= dt * 0.9))
    register("windmill", onGround(mill, x, z), x, z, 7, { r: 3.5, h: 11, y: 0 })
  }

  // Bienenstöcke auf einer Blumenwiese, mit schwirrenden Bienen
  {
    const x = 114
    const z = -16
    const meadow = group()
    const flowerColors = [0xf2a541, 0xe5624e, 0xffffff, 0xc1e130, 0xb388d9]
    for (let i = 0; i < 40; i++) {
      const a = i * 2.4
      const d = 2 + (i % 7) * 0.9
      const fx = Math.cos(a) * d
      const fz = Math.sin(a) * d
      const flower = at(box(0.3, 0.3, 0.3, flowerColors[i % flowerColors.length]), fx, heightAt(x + fx, z + fz) - heightAt(x, z) + 0.15, fz)
      flower.castShadow = false
      meadow.add(flower)
    }
    const hives = [[-1.4, 0], [0.4, -0.8], [1.9, 0.6]].map(([hx, hz]) => {
      const hive = group(box(1, 0.9, 0.8, 0xf2c14e), at(box(1.1, 0.12, 0.9, WOOD), 0, 0.9, 0), at(box(1, 0.6, 0.8, 0xe8b13a), 0, 1.02, 0))
      hive.position.set(hx, heightAt(x + hx, z + hz) - heightAt(x, z), hz)
      meadow.add(hive)
      return hive
    })
    const beeMaterial = mat(0xf7c531, { emissive: 0xf7c531, emissiveIntensity: 0.5 })
    const bees = Array.from({ length: 14 }, (_, i) => {
      const bee = new THREE.Mesh(new THREE.SphereGeometry(0.17, 5, 4), beeMaterial)
      bee.scale.set(1.4, 1, 1)
      bee.userData = { hive: hives[i % hives.length], radius: 1 + (i % 4) * 0.6, speed: 1.5 + (i % 5) * 0.4, phase: i * 1.7 }
      meadow.add(bee)
      return bee
    })
    updaters.push((dt, time) => {
      for (const bee of bees) {
        const { hive, radius, speed, phase } = bee.userData
        const t = time * speed + phase
        bee.position.set(hive.position.x + Math.cos(t) * radius, hive.position.y + 1.6 + Math.sin(t * 2.3) * 0.5, hive.position.z + Math.sin(t * 1.3) * radius)
      }
    })
    register("bees", onGround(meadow, x, z), x, z, 7, { r: 5, h: 3.5, y: 0 })
  }

  // Schneemann am Gipfelhang
  {
    const x = 20.6
    const z = -122.3
    const snowman = group()
    const snow = mat(0xffffff)
    for (const [r, y] of [[0.9, 0.75], [0.65, 1.95], [0.45, 2.85]]) {
      const ball = new THREE.Mesh(new THREE.IcosahedronGeometry(r, 1), snow)
      ball.position.y = y
      ball.castShadow = true
      snowman.add(ball)
    }
    const nose = new THREE.Mesh(new THREE.ConeGeometry(0.1, 0.5, 5), mat(COLORS.orange))
    nose.rotation.x = Math.PI / 2
    nose.position.set(0, 2.85, 0.65)
    snowman.add(nose)
    snowman.add(at(cylinder(0.45, 0.45, 0.08, COLORS.navy, 10), 0, 3.2, 0))
    snowman.add(at(cylinder(0.3, 0.3, 0.5, COLORS.navy, 10), 0, 3.25, 0))
    const scarf = new THREE.Mesh(new THREE.TorusGeometry(0.5, 0.12, 5, 12), mat(COLORS.lime))
    scarf.rotation.x = Math.PI / 2
    scarf.position.y = 2.45
    snowman.add(scarf)
    for (const side of [-1, 1]) {
      const arm = at(box(0.08, 1.1, 0.08, WOOD), side * 0.85, 1.6, 0)
      arm.rotation.z = side * -0.9
      snowman.add(arm)
    }
    // Schlitten daneben
    const sled = group(at(box(1.6, 0.12, 0.7, 0xc0703f), 0, 0.25, 0))
    for (const sz of [-0.3, 0.3]) sled.add(at(box(1.8, 0.08, 0.08, COLORS.steelDark), 0.05, 0.05, sz))
    sled.position.set(1.8, 0, 0.6)
    sled.rotation.y = 0.5
    snowman.add(sled)
    snowman.rotation.y = 0.2
    updaters.push((dt, time) => (snowman.rotation.z = Math.sin(time * 0.8) * 0.03))
    register("snowman", onGround(snowman, x, z, -0.1), x, z, 5, { r: 2.6, h: 4.2, y: 0 })
  }

  // Heißluftballon in be2morrow-Farben, der langsam über die Landschaft zieht
  {
    const balloon = new THREE.Group()
    const envelope = new THREE.Mesh(new THREE.IcosahedronGeometry(3, 1), mat(COLORS.lime))
    envelope.scale.set(1, 1.15, 1)
    envelope.position.y = 5.2
    envelope.castShadow = true
    const skirt = new THREE.Mesh(new THREE.ConeGeometry(2.3, 2.6, 10, 1, true), mat(COLORS.green, { side: THREE.DoubleSide }))
    skirt.rotation.x = Math.PI
    skirt.position.y = 2.4
    const band = new THREE.Mesh(new THREE.TorusGeometry(2.95, 0.25, 4, 18), mat(COLORS.navy))
    band.rotation.x = Math.PI / 2
    band.position.y = 4.9
    const basket = at(box(0.9, 0.7, 0.9, WOOD), 0, 0, 0)
    balloon.add(envelope, skirt, band, basket)
    for (const [rx, rz] of [[-0.4, -0.4], [0.4, -0.4], [-0.4, 0.4], [0.4, 0.4]]) balloon.add(at(box(0.04, 1.4, 0.04, COLORS.navy600), rx, 0.7, rz))
    updaters.push((dt, time) => {
      const a = time * 0.035 + 1.2
      balloon.position.set(Math.cos(a) * 92, 44 + Math.sin(time * 0.5) * 1.5, Math.sin(a) * 78)
      balloon.rotation.y = Math.sin(time * 0.2) * 0.4
    })
    register("balloon", balloon, 9999, 9999, 0, { r: 3.6, h: 9, y: 0 })
  }

  return {
    group: root,
    eggs,
    keepOut,
    update(dt, time) {
      for (const fn of updaters) fn(dt, time)
    }
  }
}

// Sucht für einen Raycast-Treffer das zugehörige Fundstück.
export function eggFor(object) {
  let current = object
  while (current) {
    if (current.userData?.egg) return { ...current.userData.egg, object: current }
    current = current.parent
  }
  return null
}
